import { useLayoutEffect, useState, type CSSProperties } from "react";

// Match the shell's mobile breakpoint; coarse pointers also cover a rotated phone.
const MOBILE_VIEWPORT = "(max-width: 639px), (pointer: coarse)";

/** Opt-in only: Safari's keyboard resizes/pans the visual, not layout, viewport. */
export function useModalVisualViewport(enabled: boolean, preserveMotion = false, mobileQuery = MOBILE_VIEWPORT): CSSProperties | undefined {
  const [style, setStyle] = useState<CSSProperties>();

  useLayoutEffect(() => {
    if (!enabled) {
      setStyle(undefined);
      return;
    }
    const media = window.matchMedia(mobileQuery);
    const viewport = window.visualViewport;
    const update = () => {
      if (!media.matches) {
        setStyle(undefined);
        return;
      }
      // Older browsers still get a bounded sheet using the resized window.
      const height = viewport?.height || window.innerHeight;
      const width = viewport?.width || window.innerWidth;
      const top = viewport?.offsetTop ?? 0;
      const left = viewport?.offsetLeft ?? 0;
      setStyle({
        top: "auto",
        bottom: window.innerHeight - top - height + 8,
        left,
        right: "auto",
        width,
        maxWidth: "none",
        maxHeight: Math.max(0, height - 16),
        // A gesture-controlled sheet owns its transforms. Header search instead
        // disables its entrance translation so autofocus cannot land offscreen.
        ...(preserveMotion ? {} : {
          transform: "none",
          translate: "none",
          animation: "none",
        }),
        paddingTop: "env(safe-area-inset-top)",
      });
    };
    update();
    media.addEventListener("change", update);
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    viewport?.addEventListener("resize", update);
    viewport?.addEventListener("scroll", update);
    return () => {
      media.removeEventListener("change", update);
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
      viewport?.removeEventListener("resize", update);
      viewport?.removeEventListener("scroll", update);
    };
  }, [enabled, preserveMotion, mobileQuery]);

  return enabled ? style : undefined;
}
